import type React from 'react';
import { useState, useRef, useEffect } from 'react';

import { ReactComponent as Arrow } from '../../assets/icons/chevron-down.svg';
import { ReactComponent as World } from '../../assets/icons/world.svg';

import {
  SContainer,
  SDropdown,
  SRegionItem,
  STextFieldEndPlaceHolder,
  SPropertyDisplay,
} from './RegionDropdown.styles';
import { translate } from '../../utils/translate';
import { RegionEnum } from '../../config/region';

const regionsMapper: Record<RegionEnum, { nameTx: string; code: string }> = {
  [RegionEnum.EU]: {
    nameTx: 'europe',
    code: 'eu',
  },
  [RegionEnum.SE]: {
    nameTx: 'sweden',
    code: 'se',
  },
  [RegionEnum.BR]: {
    nameTx: 'brazil',
    code: 'br',
  },
};

type RegionDropdownProps = {
  onChangeRegion: (region: RegionEnum) => void;
  regions: RegionEnum[];
  isError?: boolean;
  currentRegion?: RegionEnum;
};

export const RegionDropdown: React.FC<RegionDropdownProps> = ({
  onChangeRegion,
  regions,
  isError,
  currentRegion,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedRegion, setSelectedRegion] = useState<RegionEnum | undefined>(currentRegion);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const handleRegionSelect = (region: RegionEnum) => {
    setSelectedRegion(region);
    onChangeRegion(region);
    setIsOpen(false);
  };

  return (
    <SContainer ref={dropdownRef}>
      <STextFieldEndPlaceHolder className="wrapper" onClick={toggleDropdown} role="dropdown-region">
        <SPropertyDisplay $isError={isError} role="inputField">
          <label htmlFor="region-select" role="label">
            <World />
          </label>
          <input
            type="text"
            id="region-select"
            value={selectedRegion ? translate(regionsMapper[selectedRegion].nameTx) : ''}
            readOnly
            style={{ display: 'none' }}
          />
          {translate(selectedRegion ? regionsMapper[selectedRegion].nameTx : 'login_select_region')}
          <Arrow className={`arrow ${isOpen ? 'open' : ''}`} />
        </SPropertyDisplay>
      </STextFieldEndPlaceHolder>

      {isOpen && (
        <SDropdown>
          {regions.map(region => (
            <SRegionItem key={region} onClick={() => handleRegionSelect(region)}>
              {translate(regionsMapper[region].nameTx)}
            </SRegionItem>
          ))}
        </SDropdown>
      )}
    </SContainer>
  );
};
